import * as React from 'react'

import {
  ChevronDownIcon,
  ChevronUpIcon,
  Flex,
  IconButton,
  Kbd,
} from '@buffer-mono/popcorn'

import type { PostListItem } from '../types'

import styles from './PostNavigationArrows.module.css'

import type { JSX } from 'react'

type PostNavigationArrowsProps = {
  posts: PostListItem[]
  selectedPostId: string
  onPostSelect: (postId: string) => void
  /** Layout direction. 'vertical' (default) stacks buttons in a column, 'horizontal' lays them in a row. Icons are always up/down. */
  direction?: 'vertical' | 'horizontal'
}

export const PostNavigationArrows = ({
  posts,
  selectedPostId,
  onPostSelect,
  direction = 'vertical',
}: PostNavigationArrowsProps): JSX.Element => {
  const currentIndex = React.useMemo(
    () => posts.findIndex((p) => p.resolved.id === selectedPostId),
    [posts, selectedPostId],
  )

  const hasPrev = currentIndex > 0
  const hasNext = currentIndex !== -1 && currentIndex < posts.length - 1

  const handlePrev = React.useCallback(() => {
    if (currentIndex === -1) return
    if (hasPrev) {
      onPostSelect(posts[currentIndex - 1].resolved.id)
    }
  }, [hasPrev, posts, onPostSelect, currentIndex])

  const handleNext = React.useCallback(() => {
    if (currentIndex === -1) return
    if (hasNext) {
      onPostSelect(posts[currentIndex + 1].resolved.id)
    }
  }, [hasNext, posts, onPostSelect, currentIndex])

  const isHorizontal = direction === 'horizontal'

  return (
    <Flex
      direction={isHorizontal ? 'row' : 'column'}
      gap="xs"
      align="center"
      className={
        isHorizontal
          ? styles.navigationArrowsHorizontal
          : styles.navigationArrows
      }
    >
      <IconButton
        label="Previous post"
        size="medium"
        variant="secondary"
        disabled={!hasPrev}
        onClick={handlePrev}
        tooltip={
          <>
            Previous post <Kbd color="inverted">mod</Kbd>{' '}
            <Kbd color="inverted">↑</Kbd>
          </>
        }
        className={styles.navigationButton}
      >
        <ChevronUpIcon />
      </IconButton>
      <IconButton
        label="Next post"
        size="medium"
        variant="secondary"
        disabled={!hasNext}
        onClick={handleNext}
        tooltip={
          <>
            Next post <Kbd color="inverted">mod</Kbd>{' '}
            <Kbd color="inverted">↓</Kbd>
          </>
        }
        className={styles.navigationButton}
      >
        <ChevronDownIcon />
      </IconButton>
    </Flex>
  )
}
